import { Box, Flex, Link, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { StepTitle } from '../../v2qs/step-title';

interface GuideCardProps {
  icon: React.ReactNode;
  title: string;
  href: string;
}

const GuideCard: React.FC<GuideCardProps> = ({ icon, title, href }) => {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      display="block"
      w="full"
      _hover={{ textDecoration: 'none' }}
    >
      <Box
        display="grid"
        gridTemplateRows="1fr 1fr"
        h="120px"
        p={4}
        bg="gray.50"
        borderRadius="xl"
        border="1px solid"
        borderColor="gray.200"
        transition="all 0.2s"
        _hover={{
          bg: 'green.50',
          borderColor: 'green.200',
          transform: 'translateY(-2px)',
          boxShadow: 'sm',
        }}
      >
        <Flex align="center" justify="center" color="green.500">
          {icon}
        </Flex>
        <Flex align="flex-start" justify="center">
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color="green.600"
            textAlign="center"
            lineHeight="1.3"
          >
            {title}
          </Text>
        </Flex>
      </Box>
    </Link>
  );
};

// Simple icon components
const TiltZoomIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8" />
    <path d="M12 17v4" />
    <path d="M7 10l3-3 3 3" />
    <path d="M17 10l-3-3-3 3" />
  </svg>
);

const TipsIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z" />
  </svg>
);

const SettingsIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
  </svg>
);

const LightIndicatorIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="5" y="2" width="14" height="20" rx="2" />
    <circle cx="12" cy="8" r="2" fill="currentColor" />
    <circle cx="8" cy="14" r="1" />
    <circle cx="12" cy="14" r="1" />
    <circle cx="16" cy="14" r="1" />
  </svg>
);

const TroubleshootIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
    <path d="M11 8v6M11 16h.01" />
  </svg>
);

const ResetIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
  </svg>
);

export const GuidesHelpStep: React.FC = () => {
  const guides = [
    {
      icon: <TiltZoomIcon />,
      title: 'How to Adjust Tilt & Zoom',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510498-how-to-adjust-v2-camera-settings-with-frogbox-connect',
    },
    {
      icon: <TipsIcon />,
      title: 'Tips for Best Results',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510781-tips-for-best-results-with-v2-cameras',
    },
    {
      icon: <SettingsIcon />,
      title: 'Advanced Camera Settings',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510784-frogbox-connect-advanced-camera-settings',
    },
    {
      icon: <LightIndicatorIcon />,
      title: 'Camera Light Indicators',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510773-v2-camera-light-indicators',
    },
    {
      icon: <TroubleshootIcon />,
      title: 'Camera Troubleshooting',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510777-v2-camera-troubleshooting',
    },
    {
      icon: <ResetIcon />,
      title: 'Factory Reset',
      href: 'https://frogbox.freshdesk.com/support/solutions/articles/51000510779-how-to-complete-a-factory-reset-on-v2-camera',
    },
  ];

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      px={6}
      pt={16}
      pb={24}
      bg="white"
      overflowY="auto"
    >
      <VStack gap={5} maxW="500px" w="full">
        <StepTitle stepNumber={3} title="Camera Guides & Help" />

        {/* Intro Text */}
        <Text fontSize="sm" color="gray.800" textAlign="center">
          Tap any guide below for quick answers and handy tips:
        </Text>

        {/* Guide Cards Grid */}
        <SimpleGrid columns={2} gap={3} w="full" maxW="400px">
          {guides.map((guide, index) => (
            <GuideCard
              key={index}
              icon={guide.icon}
              title={guide.title}
              href={guide.href}
            />
          ))}
        </SimpleGrid>
      </VStack>
    </Flex>
  );
};
